import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../utils/api';

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const invoiceId = searchParams.get('invoice_id');
  const token = searchParams.get('token');
  // Flow A (booking checkout) has no invoice to fetch, so the paid total is
  // passed in the success URL as conv_value (see server/routes/payment.js).
  const convValue = searchParams.get('conv_value');
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fire conversion tracking once a payment is confirmed. Two events:
  //   • GA4 `purchase` — for Google Analytics revenue/behavior reporting.
  //   • Google Ads `conversion` (AW-18123851140 / nlMOCLivnrEcEISLkMJD) — the
  //     Primary "Purchase" action that drives Google Ads / PMax bidding.
  // Both share the same transaction_id so GA4 and Ads each de-dup on reloads.
  // The GA4 purchase event is NOT imported into Ads, so there's no double
  // count — Ads counts only the explicit conversion event. Wrapped in
  // try/catch: a tracking failure must never surface to the customer.
  const fireConversions = (value, transactionId, itemId) => {
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'purchase', {
          transaction_id: transactionId,
          value: value,
          currency: 'USD',
          items: [{
            item_id: itemId || 'photography',
            item_name: 'Real Estate Photography',
            item_category: 'photography',
            price: value,
            quantity: 1
          }]
        });
        window.gtag('event', 'conversion', {
          send_to: 'AW-18123851140/nlMOCLivnrEcEISLkMJD',
          value: value,
          currency: 'USD',
          transaction_id: transactionId
        });
      }
    } catch (trackErr) {
      console.warn('Conversion tracking failed (non-fatal):', trackErr?.message || trackErr);
    }
  };

  useEffect(() => {
    if (invoiceId) {
      // Flow B (invoice payment): fetch the invoice, then fire with its amount.
      fetchInvoice();
    } else {
      // Flow A (booking checkout): no invoice to fetch — the paid total arrives
      // via conv_value, so the conversion still fires (previously it did not).
      if (convValue) {
        const value = Number(convValue) || 0;
        const transactionId = sessionId || `booking-${Date.now()}`;
        fireConversions(value, transactionId, 'booking');
      }
      setLoading(false);
    }
    // Pack J.1: fire-and-forget sub check for the upsell card visibility.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      const response = await api.get(`/invoice/${invoiceId}${token ? `?token=${token}` : ''}`);
      const inv = response.data.data;
      setInvoice(inv);

      if (inv) {
        const transactionId = sessionId || `inv-${inv._id}-${inv.amountPaid || inv.total || 0}`;
        const value = Number(inv.amountPaid) || Number(inv.total) || 0;
        fireConversions(value, transactionId, inv._id || 'invoice');
      }
    } catch (err) {
      console.error('Error fetching invoice:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ paddingTop: 'var(--page-top-offset)', minHeight: '100vh', textAlign: 'center' }}>
        <p>Confirming payment...</p>
      </div>
    );
  }

  const isPartiallyPaid = invoice?.status === 'partially_paid';
  const isFullyPaid = invoice?.status === 'paid';

  return (
    <div style={{ paddingTop: 'var(--page-top-offset)', minHeight: '100vh' }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '40px',
        textAlign: 'center'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: '#d4edda',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 25px',
          fontSize: '40px'
        }}>
          ✓
        </div>

        <h1 style={{ fontSize: '28px', marginBottom: '10px', color: '#155724' }}>
          Payment Successful!
        </h1>

        {invoice && (
          <div style={{
            background: '#f8f9fa',
            borderRadius: '12px',
            padding: '25px',
            margin: '25px 0',
            textAlign: 'left'
          }}>
            <p style={{ margin: '0 0 8px', fontSize: '14px', color: '#666' }}>
              Invoice #{invoice.invoiceNumber}
            </p>
            {isPartiallyPaid && (
              <>
                <p style={{ margin: '8px 0', fontSize: '16px' }}>
                  <strong>Deposit paid:</strong> ${(invoice.amountPaid || 0).toFixed(2)}
                </p>
                <p style={{ margin: '8px 0', fontSize: '16px' }}>
                  <strong>Remaining balance:</strong> ${(invoice.balanceDue || 0).toFixed(2)}
                </p>
                <p style={{ margin: '12px 0 0', fontSize: '14px', color: '#666' }}>
                  You'll be notified when the remaining balance is due.
                </p>
              </>
            )}
            {isFullyPaid && (
              <p style={{ margin: '8px 0', fontSize: '16px', color: '#155724', fontWeight: '600' }}>
                Invoice paid in full — ${(invoice.total || 0).toFixed(2)}
              </p>
            )}
            {!isPartiallyPaid && !isFullyPaid && (
              <p style={{ margin: '8px 0', fontSize: '14px', color: '#666' }}>
                Your payment is being processed. The invoice will update shortly.
              </p>
            )}
          </div>
        )}

        <p style={{ color: '#666', fontSize: '15px', marginBottom: '30px' }}>
          A confirmation email has been sent to your email address.
        </p>

        {/* Prepaid credit — replaces the retired subscription upsell. */}
        <div style={{
          background: 'linear-gradient(135deg, #fef9c3 0%, #fef3c7 100%)',
          border: '2px solid #f59e0b', borderRadius: '12px',
          padding: '20px 24px', marginBottom: '30px', textAlign: 'left'
        }}>
          <h3 style={{ color: '#78350f', margin: '0 0 8px', fontSize: '1.1rem' }}>Booking more shoots this year?</h3>
          <p style={{ color: '#92400e', margin: '0 0 14px', fontSize: '0.95rem' }}>
            Prepaid credit packs add bonus credit on top of what you put in. It never expires and works on every package and add-on.
          </p>
          <Link to="/credit" style={{
            display: 'inline-block', padding: '10px 22px', background: '#d97706',
            color: 'white', borderRadius: '6px', textDecoration: 'none', fontWeight: '600', fontSize: '14px'
          }}>
            See credit packs
          </Link>
        </div>

        <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link
            to="/"
            style={{
              padding: '12px 30px',
              background: '#667eea',
              color: 'white',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '15px'
            }}
          >
            Back to Home
          </Link>
          <Link
            to="/client/portal"
            style={{
              padding: '12px 30px',
              background: 'white',
              color: '#667eea',
              border: '2px solid #667eea',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '15px'
            }}
          >
            Client Portal
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;
